(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const WISHLIST_KEY = 'adWishlistV1';
  const RECENT_KEY = 'adRecentlyViewedV1';
  const MAX_WISHLIST = 60;
  const MAX_RECENT = 16;
  const memory = new Map();

  const safeParse = (value, fallback = []) => {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (error) {
      return fallback;
    }
  };

  const readStore = (key) => {
    try {
      const value = window.localStorage.getItem(key);
      return value === null ? (memory.get(key) || []) : safeParse(value, []);
    } catch (error) {
      return memory.get(key) || [];
    }
  };

  const writeStore = (key, value) => {
    memory.set(key, value);
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (error) {}
  };

  const normalizeRecord = (record = {}) => ({
    id: String(record.id || ''),
    title: String(record.title || ''),
    url: String(record.url || ''),
    image: String(record.image || ''),
    price: String(record.price || ''),
    type: String(record.type || ''),
    variantId: String(record.variantId || ''),
    singleVariant: record.singleVariant === true || record.singleVariant === 'true',
    available: record.available === true || record.available === 'true',
    savedAt: Number(record.savedAt || Date.now())
  });

  const recordFromElement = (element) => {
    const source = element?.closest?.('[data-aph-product-record]') || element;
    if (!source?.dataset?.aphProductId) return null;
    return normalizeRecord({
      id: source.dataset.aphProductId,
      title: source.dataset.aphProductTitle,
      url: source.dataset.aphProductUrl,
      image: source.dataset.aphProductImage,
      price: source.dataset.aphProductPrice,
      type: source.dataset.aphProductType,
      variantId: source.dataset.aphProductVariantId,
      singleVariant: source.dataset.aphProductSingleVariant,
      available: source.dataset.aphProductAvailable,
      savedAt: Date.now()
    });
  };

  const setRecordDataset = (element, record) => {
    element.dataset.aphProductRecord = '';
    element.dataset.aphProductId = record.id;
    element.dataset.aphProductTitle = record.title;
    element.dataset.aphProductUrl = record.url;
    element.dataset.aphProductImage = record.image;
    element.dataset.aphProductPrice = record.price;
    element.dataset.aphProductType = record.type;
    element.dataset.aphProductVariantId = record.variantId;
    element.dataset.aphProductSingleVariant = String(record.singleVariant);
    element.dataset.aphProductAvailable = String(record.available);
  };

  const getWishlist = () => readStore(WISHLIST_KEY).map(normalizeRecord).filter((item) => item.id);
  const getRecent = () => readStore(RECENT_KEY).map(normalizeRecord).filter((item) => item.id);
  const isSaved = (id) => getWishlist().some((item) => item.id === String(id));

  const shopAllUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/$/, '')}/collections/all`;
  };

  const syncOverlayState = () => {
    const anyOpen = qsa('dialog[open]').length > 0;
    document.body?.classList.toggle('aph-overlay-open', anyOpen || document.body?.classList.contains('aph-menu-open'));
  };

  const openWishlist = () => {
    const dialog = qs('[data-aph-wishlist-dialog]');
    if (!dialog) return;
    const mobile = qs('[data-aph-mobile-dialog][open]');
    if (mobile) {
      if (typeof mobile.close === 'function') mobile.close(); else mobile.removeAttribute('open');
      document.body?.classList.remove('aph-menu-open');
      qs('[data-aph-mobile-open]')?.setAttribute('aria-expanded', 'false');
    }
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    else dialog.setAttribute('open', '');
    syncOverlayState();
    window.setTimeout(() => qs('[data-aph-wishlist-close]', dialog)?.focus(), 20);
  };

  const closeWishlist = () => {
    const dialog = qs('[data-aph-wishlist-dialog]');
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    syncOverlayState();
  };

  const announce = (message) => {
    qsa('[data-aph-saved-status]').forEach((node) => { node.textContent = message; });
  };

  const makeHeartMark = () => {
    const mark = document.createElement('span');
    mark.className = 'aph-saved-card__heart';
    mark.setAttribute('aria-hidden', 'true');
    mark.textContent = '♡';
    return mark;
  };

  const makeSavedCard = (record, context = 'page') => {
    const card = document.createElement('article');
    card.className = `aph-saved-card aph-saved-card--${context}`;
    setRecordDataset(card, record);

    const mediaWrap = document.createElement('div');
    mediaWrap.className = 'aph-saved-card__media';
    const mediaLink = document.createElement('a');
    mediaLink.className = 'aph-saved-card__image-link';
    mediaLink.href = record.url || '#';
    mediaLink.setAttribute('aria-label', record.title || 'View product');
    if (record.image) {
      const image = document.createElement('img');
      image.src = record.image;
      image.alt = record.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      mediaLink.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'aph-saved-card__placeholder';
      placeholder.textContent = 'Aphantastic';
      mediaLink.appendChild(placeholder);
    }
    mediaWrap.appendChild(mediaLink);

    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'aph-wishlist-toggle aph-wishlist-toggle--saved-card';
    save.dataset.aphWishlistToggle = '';
    save.setAttribute('aria-label', `Remove ${record.title} from wishlist`);
    save.setAttribute('aria-pressed', 'true');
    save.classList.add('is-saved');
    save.appendChild(makeHeartMark());
    mediaWrap.appendChild(save);

    const body = document.createElement('div');
    body.className = 'aph-saved-card__body';
    if (record.type) {
      const type = document.createElement('span');
      type.className = 'aph-saved-card__type';
      type.textContent = record.type;
      body.appendChild(type);
    }
    const title = document.createElement('h3');
    title.className = 'aph-saved-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = record.url || '#';
    titleLink.textContent = record.title || 'Saved product';
    title.appendChild(titleLink);
    body.appendChild(title);
    if (record.price) {
      const price = document.createElement('p');
      price.className = 'aph-saved-card__price';
      price.textContent = record.price;
      body.appendChild(price);
    }
    const actions = document.createElement('div');
    actions.className = 'aph-saved-card__actions';
    const view = document.createElement('a');
    view.className = 'aph-btn aph-saved-card__view';
    view.href = record.url || '#';
    view.textContent = 'View product';
    actions.appendChild(view);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'aph-link-button aph-saved-card__remove';
    remove.dataset.aphWishlistRemove = record.id;
    remove.textContent = 'Remove';
    actions.appendChild(remove);
    body.appendChild(actions);

    card.append(mediaWrap, body);
    return card;
  };

  const makeRecentCard = (record) => {
    const card = document.createElement('article');
    card.className = 'aph-recent-card';
    setRecordDataset(card, record);

    const media = document.createElement('div');
    media.className = 'aph-recent-card__media';
    const mediaLink = document.createElement('a');
    mediaLink.className = 'aph-recent-card__image-link';
    mediaLink.href = record.url || '#';
    mediaLink.setAttribute('aria-label', record.title || 'View product');
    if (record.image) {
      const image = document.createElement('img');
      image.src = record.image;
      image.alt = record.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      mediaLink.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'aph-saved-card__placeholder';
      placeholder.textContent = 'Aphantastic';
      mediaLink.appendChild(placeholder);
    }
    media.appendChild(mediaLink);

    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'aph-wishlist-toggle aph-wishlist-toggle--recent';
    save.dataset.aphWishlistToggle = '';
    save.setAttribute('aria-label', `Save ${record.title} to wishlist`);
    save.setAttribute('aria-pressed', 'false');
    save.appendChild(makeHeartMark());
    media.appendChild(save);

    const body = document.createElement('div');
    body.className = 'aph-recent-card__body';
    if (record.type) {
      const type = document.createElement('span');
      type.className = 'aph-recent-card__type';
      type.textContent = record.type;
      body.appendChild(type);
    }
    const title = document.createElement('h3');
    title.className = 'aph-recent-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = record.url || '#';
    titleLink.textContent = record.title || 'Recently viewed product';
    title.appendChild(titleLink);
    body.appendChild(title);
    if (record.price) {
      const price = document.createElement('p');
      price.className = 'aph-recent-card__price';
      price.textContent = record.price;
      body.appendChild(price);
    }
    card.append(media, body);
    return card;
  };

  const renderWishlistSurfaces = () => {
    const items = getWishlist();
    qsa('[data-aph-wishlist-count]').forEach((node) => {
      node.textContent = String(items.length);
      node.hidden = items.length === 0;
    });
    qsa('[data-aph-wishlist-count-inline]').forEach((node) => { node.textContent = String(items.length); });
    qsa('[data-aph-wishlist-count-label]').forEach((node) => { node.textContent = items.length === 1 ? 'piece' : 'pieces'; });

    qsa('[data-aph-wishlist-surface]').forEach((surface) => {
      surface.replaceChildren();
      const context = surface.dataset.aphWishlistSurface === 'drawer' ? 'drawer' : 'page';
      if (!items.length) {
        const empty = document.createElement('div');
        empty.className = 'aph-wishlist-empty';
        const mark = document.createElement('span');
        mark.className = 'aph-wishlist-empty__mark';
        mark.setAttribute('aria-hidden', 'true');
        mark.textContent = '♡';
        const title = document.createElement('h3');
        title.textContent = 'Nothing saved yet';
        const text = document.createElement('p');
        text.textContent = 'Tap the heart on anything that makes you stop scrolling.';
        const shop = document.createElement('a');
        shop.className = 'aph-btn';
        shop.href = shopAllUrl();
        shop.textContent = 'Explore the shop';
        empty.append(mark, title, text, shop);
        surface.appendChild(empty);
        return;
      }
      const grid = document.createElement('div');
      grid.className = `aph-wishlist-grid aph-wishlist-grid--${context}`;
      items.forEach((item) => grid.appendChild(makeSavedCard(item, context)));
      surface.appendChild(grid);
    });

    qsa('[data-aph-wishlist-clear]').forEach((button) => { button.hidden = items.length === 0; });
    syncToggleStates();
  };

  const syncToggleStates = () => {
    const savedIds = new Set(getWishlist().map((item) => item.id));
    qsa('[data-aph-wishlist-toggle]').forEach((button) => {
      const record = recordFromElement(button);
      if (!record) return;
      const active = savedIds.has(record.id);
      button.classList.toggle('is-saved', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? 'Remove' : 'Save'} ${record.title || 'product'} ${active ? 'from' : 'to'} wishlist`);
      const mark = qs('.aph-saved-card__heart', button);
      if (mark) mark.textContent = active ? '♥' : '♡';
      const text = qs('span:not(.aph-saved-card__heart)', button);
      if (text && button.classList.contains('aph-wishlist-toggle--pdp')) text.textContent = active ? 'Saved' : 'Save';
    });
  };

  const toggleWishlist = (record) => {
    if (!record?.id) return;
    const items = getWishlist();
    const index = items.findIndex((item) => item.id === record.id);
    let message = '';
    if (index >= 0) {
      items.splice(index, 1);
      message = `${record.title} removed from wishlist.`;
    } else {
      items.unshift({ ...record, savedAt: Date.now() });
      if (items.length > MAX_WISHLIST) items.length = MAX_WISHLIST;
      message = `${record.title} saved to wishlist.`;
    }
    writeStore(WISHLIST_KEY, items);
    renderWishlistSurfaces();
    announce(message);
    document.dispatchEvent(new CustomEvent('ad:wishlist-change', { detail: { items } }));
  };

  const removeWishlistItem = (id) => {
    const items = getWishlist();
    const removed = items.find((item) => item.id === String(id));
    const next = items.filter((item) => item.id !== String(id));
    writeStore(WISHLIST_KEY, next);
    renderWishlistSurfaces();
    if (removed) announce(`${removed.title} removed from wishlist.`);
  };

  const clearWishlist = () => {
    writeStore(WISHLIST_KEY, []);
    renderWishlistSurfaces();
    announce('Wishlist cleared.');
  };

  const recordRecentlyViewed = (scope = document) => {
    const root = qs('[data-aph-product-root][data-aph-product-record]', scope) || (scope.matches?.('[data-aph-product-root][data-aph-product-record]') ? scope : null);
    if (!root) return;
    const record = recordFromElement(root);
    if (!record?.id) return;
    const items = getRecent().filter((item) => item.id !== record.id);
    items.unshift({ ...record, savedAt: Date.now() });
    if (items.length > MAX_RECENT) items.length = MAX_RECENT;
    writeStore(RECENT_KEY, items);
  };

  const renderRecentSection = (section) => {
    const track = qs('[data-aph-recent-track]', section);
    if (!track) return;
    const currentId = String(section.dataset.currentProductId || '');
    const limit = Math.max(1, Number(section.dataset.limit || 8));
    const items = getRecent().filter((item) => item.id !== currentId).slice(0, limit);
    track.replaceChildren();
    if (!items.length) {
      section.hidden = true;
      return;
    }
    items.forEach((item) => track.appendChild(makeRecentCard(item)));
    section.hidden = false;
    syncToggleStates();
    updateRecentControls(section);
  };

  const updateRecentControls = (section) => {
    const track = qs('[data-aph-recent-track]', section);
    const progress = qs('[data-aph-recent-progress]', section);
    if (!track) return;
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    const ratio = max <= 1 ? 1 : Math.min(1, Math.max(.08, (track.scrollLeft + track.clientWidth) / track.scrollWidth));
    if (progress) progress.style.transform = `scaleX(${ratio})`;
    const prev = qs('[data-aph-recent-prev]', section);
    const next = qs('[data-aph-recent-next]', section);
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  };

  const recentStep = (track) => {
    const card = qs('.aph-recent-card', track);
    if (!card) return Math.max(240, track.clientWidth * .7);
    const style = getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap || 0);
    return card.getBoundingClientRect().width + gap;
  };

  const initRecentSection = (section) => {
    if (section.dataset.aphRecentBound === 'true') {
      renderRecentSection(section);
      return;
    }
    section.dataset.aphRecentBound = 'true';
    const track = qs('[data-aph-recent-track]', section);
    const prev = qs('[data-aph-recent-prev]', section);
    const next = qs('[data-aph-recent-next]', section);
    prev?.addEventListener('click', () => track?.scrollBy({ left: -recentStep(track), behavior: 'smooth' }));
    next?.addEventListener('click', () => track?.scrollBy({ left: recentStep(track), behavior: 'smooth' }));
    track?.addEventListener('scroll', () => requestAnimationFrame(() => updateRecentControls(section)), { passive: true });
    track?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); track.scrollBy({ left: -recentStep(track), behavior: 'smooth' }); }
      if (event.key === 'ArrowRight') { event.preventDefault(); track.scrollBy({ left: recentStep(track), behavior: 'smooth' }); }
    });
    if ('ResizeObserver' in window && track) {
      const observer = new ResizeObserver(() => updateRecentControls(section));
      observer.observe(track);
      section._adRecentObserver = observer;
    }
    renderRecentSection(section);
  };

  const renderAll = (scope = document) => {
    renderWishlistSurfaces();
    qsa('[data-aph-recently-viewed]', scope).forEach(initRecentSection);
    if (scope.matches?.('[data-aph-recently-viewed]')) initRecentSection(scope);
    syncToggleStates();
  };

  const init = (scope = document) => {
    recordRecentlyViewed(scope);
    renderAll(scope);
  };

  if (document.documentElement.dataset.aphSavedProductsBound !== 'true') {
    document.documentElement.dataset.aphSavedProductsBound = 'true';
    document.addEventListener('click', (event) => {
      const wishlistOpen = event.target.closest('[data-aph-wishlist-open]');
      if (wishlistOpen) {
        event.preventDefault();
        openWishlist();
        return;
      }
      const wishlistClose = event.target.closest('[data-aph-wishlist-close]');
      if (wishlistClose) {
        event.preventDefault();
        closeWishlist();
        return;
      }
      const toggle = event.target.closest('[data-aph-wishlist-toggle]');
      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        const record = recordFromElement(toggle);
        if (record) toggleWishlist(record);
        return;
      }
      const remove = event.target.closest('[data-aph-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        removeWishlistItem(remove.dataset.aphWishlistRemove);
        return;
      }
      const clear = event.target.closest('[data-aph-wishlist-clear]');
      if (clear) {
        event.preventDefault();
        clearWishlist();
      }
    });

    const dialog = qs('[data-aph-wishlist-dialog]');
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) closeWishlist(); });
    dialog?.addEventListener('close', syncOverlayState);
    window.addEventListener('storage', (event) => {
      if (event.key === WISHLIST_KEY || event.key === RECENT_KEY) renderAll(document);
    });
    document.addEventListener('ad:wishlist-change', () => qsa('[data-aph-recently-viewed]').forEach(renderRecentSection));
    document.addEventListener('ad:content-rendered', (event) => init(event.detail?.root || document));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    qsa('[data-aph-recently-viewed]', event.target).forEach((section) => section._adRecentObserver?.disconnect());
  });
})();
